import { Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { SessionService } from '@services/session.service';
import { Storage } from '@constants/storage';
import { BehaviorSubject, from, Observable, throwError } from 'rxjs';
import { catchError, filter, mergeMap, switchMap, take } from 'rxjs/operators';
import { ApiService } from '@services/api.service';
import { ActivatedRoute, Router } from '@angular/router';
import { SessionService as SessionServiceNew } from '@arkius/shared-services';
@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(
    null,
  );

  constructor(
    private router: Router,
    private session: SessionService,
    private sessionNew: SessionServiceNew,
    private api: ApiService,
  ) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    if (this.router.url.startsWith('/public/')) {
      return next.handle(request);
    }
    const accessToken = this.sessionNew.get(Storage.SESSION_TOKEN);
    if (accessToken) {
      request = this.addHeaders(request, accessToken);
      return next.handle(request).pipe(
        catchError((error) => {
          if (error instanceof HttpErrorResponse && error.status === 401) {
            return this.handle401Error(request, next);
          } else {
            return throwError(error);
          }
        }),
      );
    } else {
      request = this.addHeaders(request, null);
      return next.handle(request).pipe(
        catchError((error) => {
          if (error instanceof HttpErrorResponse && error.status === 401) {
            return from(this.session.logout()).pipe(
              mergeMap(() => throwError(error)),
            );
          } else {
            return throwError(error);
          }
        }),
      );
    }
  }

  addHeaders(request: any, data: any): any {
    if (data) {
      if (request.body instanceof Blob) {
        request = request.clone({
          setHeaders: {
            Accept: 'application/json, text/plain, */*',
          },
        });
      } else {
        request = this.addAuthHeader(request, data);
      }
    } else {
      if (request.body instanceof FormData) {
        request = request.clone({
          setHeaders: {
            Accept: 'application/json, text/plain, */*',
          },
        });
      } else {
        request = request.clone({
          setHeaders: {
            Accept: 'application/json, text/plain, */*',
            'Content-Type': 'application/json',
          },
        });
      }
    }
    return request;
  }

  addAuthHeader(request: any, data: any): any {
    if (request.body instanceof FormData) {
      return request.clone({
        setHeaders: {
          Authorization: `Bearer ${data}`,
          Accept: 'application/json, text/plain, */*',
        },
      });
    } else {
      return request.clone({
        setHeaders: {
          Authorization: `Bearer ${data}`,
          Accept: 'application/json, text/plain, */*',
          'Content-Type': 'application/json',
        },
      });
    }
  }

  private handle401Error(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<any> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      const refreshToken = this.sessionNew.get(Storage.REFRESH_TOKEN);
      if (refreshToken) {
        return this.api.auth.refreshToken.get(refreshToken).pipe(
          switchMap((result: any) => {
            this.isRefreshing = false;
            this.refreshTokenSubject.next(result.token);
            this.session.save(result).then();
            return next.handle(this.addHeaders(request, result.token));
          }),
          catchError((err) => {
            this.isRefreshing = false;
            this.session.clearSession().then();
            return from(this.session.logout()).pipe(
              mergeMap(() => throwError(err)),
            );
          }),
        );
      } else {
        return from(this.session.logout());
      }
    } else {
      return this.refreshTokenSubject.pipe(
        filter((token) => token != null),
        take(1),
        switchMap((sessionToken) => {
          return next.handle(this.addHeaders(request, sessionToken));
        }),
      );
    }
  }
}
